import React, { useContext, useEffect, useReducer, useState } from "react";

import {
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from "@material-ui/core";

import { makeStyles } from "@material-ui/core/styles";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import Title from "../../components/Title";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { DeleteOutline, Edit } from "@material-ui/icons";
import PromptModal from "../../components/PromptModal";
import { toast } from "react-toastify";
import ConfirmationModal from "../../components/ConfirmationModal";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { SocketContext } from "../../context/Socket/SocketContext";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  customTableCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  // Adicione um estilo para a box vermelha
  redBox: {
    backgroundColor: "#ffcccc", // Definindo a cor de fundo vermelha
    padding: theme.spacing(2), // Adicionando um espaçamento interno
    marginBottom: theme.spacing(2), // Adicionando margem inferior para separar do conteúdo abaixo
  },
}));

const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD_PROMPTS": {
      const prompts = action.payload;
      const newPrompts = [];

      if (!prompts || prompts.length === 0) return [];

      prompts.forEach((prompt) => {
        const idx = state.findIndex((p) => p.id === prompt.id);
        if (idx !== -1) {
          state[idx] = prompt;
        } else {
          newPrompts.push(prompt);
        }
      });

      return [...state, ...newPrompts];
    }

    case "UPDATE_PROMPTS": {
      const prompt = action.payload;
      const idx = state.findIndex((p) => p.id === prompt.id);

      if (idx !== -1) {
        state[idx] = prompt;
        return [...state];
      }
      return [prompt, ...state];
    }

    case "DELETE_PROMPT":
      return state.filter((p) => p.id !== action.payload);

    case "RESET":
      return [];

    default:
      return state;
  }
};


const Prompts = () => {
  const classes = useStyles();

  const [prompts, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);

  const [promptModalOpen, setPromptModalOpen] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const { user } = useContext(AuthContext);
  const { getPlanCompany } = usePlans();
  const history = useHistory();
  const companyId = user.companyId;

  const socketManager = useContext(SocketContext);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const planConfigs = await getPlanCompany(undefined, companyId);
        if (!planConfigs?.plan?.useOpenAi) {
          toast.error("Esta empresa não possui permissão para acessar essa página! Estamos lhe redirecionando.");
          setTimeout(() => history.push(`/`), 1000);
        }
      } catch (err) {
        toastError(err);
      }
    };
    fetchData();
  }, [getPlanCompany, companyId, history]);
  
  useEffect(() => {
    let isMounted = true;
  
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/prompt");
        if (isMounted) {
          dispatch({ type: "LOAD_PROMPTS", payload: data.prompts });
          setLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          toastError(err);
          setLoading(false);
        }
      }
    })();
  
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const socket = socketManager.getSocket(companyId);

    socket.on(`company-${companyId}-prompt`, (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_PROMPTS", payload: data.prompt });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_PROMPT", payload: data.promptId });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [companyId, socketManager]);

  const getPrompts = async (  ) => {

    const { data } = await api.get("/prompt");
    dispatch({ type: "LOAD_PROMPTS", payload: data.prompts });
  }

  const handleOpenPromptModal = () => {
    setPromptModalOpen(true);
    setSelectedPrompt(null);
  };

  const handleClosePromptModal = () => {
    setPromptModalOpen(false);
    setSelectedPrompt(null);
  };

  const handleEditPrompt = (prompt) => {
    setSelectedPrompt(prompt);
    setPromptModalOpen(true);
  };

  const handleCloseConfirmationModal = () => {
    setConfirmModalOpen(false);
    setSelectedPrompt(null);
  };

  const handleDeletePrompt = async (promptId) => {
    try {
      const { data } = await api.delete(`/prompt/${promptId}`);
      if (data?.message) {
        dispatch({ type: "DELETE_PROMPT", payload: promptId });
        toast.info(i18n.t(data.message));
      }
    } catch (err) {
      toastError(err);
    }
    setSelectedPrompt(null);
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          selectedPrompt &&
          `${i18n.t("prompts.confirmationModal.deleteTitle")} ${selectedPrompt.name
          }?`
        }
        open={confirmModalOpen}
        onClose={handleCloseConfirmationModal}
        onConfirm={() => handleDeletePrompt(selectedPrompt.id)}
      >
        {i18n.t("prompts.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <PromptModal
        open={promptModalOpen}
        onClose={handleClosePromptModal}
        promptId={selectedPrompt?.id}
        refreshPrompts={getPrompts}
      />
      <MainHeader>
        <Title>{i18n.t("prompts.title")}</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenPromptModal}
          >
            {i18n.t("prompts.buttons.add")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell align="left">{i18n.t("prompts.table.name")}</TableCell>
            <TableCell align="left">{i18n.t("prompts.table.queue")}</TableCell>
            <TableCell align="left">{i18n.t("prompts.table.model")}</TableCell>
            <TableCell align="center">{i18n.t("prompts.table.actions")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <>
            {prompts.map((prompt) => (
              <TableRow key={prompt.id}>
                <TableCell align="left">{prompt.name}</TableCell>
                <TableCell align="left">{prompt.queue?.name}</TableCell>
                <TableCell align="left">{prompt.model}</TableCell>
                <TableCell align="center">
                  <IconButton
                    size="small"
                    onClick={() => handleEditPrompt(prompt)}
                  >
                    <Edit />
                  </IconButton>

                  <IconButton
                    size="small"
                    onClick={() => {
                      setSelectedPrompt(prompt);
                      setConfirmModalOpen(true);
                    }}
                  >
                    <DeleteOutline />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {loading && <TableRowSkeleton columns={4} />}
          </>
        </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default Prompts;
